(() => {
  const featureName = 'tag-preview';
  const fieldNames = {
    dog_name: 'properties[שם הכלב]',
    phone: 'properties[מספר טלפון]'
  };
  const colorKeys = ['black', 'beige', 'red', 'blue', 'pink', 'orange', 'aqua', 'grey'];
  const desktopQuery = '(min-width: 990px)';

  const interpolate = (template, name, phone) => String(template || '')
    .split('[name]').join(name)
    .split('[phone]').join(phone);

  const dataKey = (prefix, colorKey) => `${prefix}${colorKey[0].toUpperCase()}${colorKey.slice(1)}`;

  const fitLine = (output) => {
    output.style.removeProperty('font-size');
    if (!output.textContent || output.offsetParent === null) return;

    const availableWidth = output.clientWidth;
    const requiredWidth = output.scrollWidth;
    if (!availableWidth || requiredWidth <= availableWidth) return;

    const startingSize = Number.parseFloat(window.getComputedStyle(output).fontSize);
    if (!Number.isFinite(startingSize)) return;

    const fittedSize = Math.max(6, startingSize * (availableWidth / requiredWidth) * .98);
    output.style.fontSize = `${fittedSize.toFixed(2)}px`;
  };

  const personalizedPlaceholder = (template, exampleName) => {
    const source = String(template || '').trim();
    if (!source) return exampleName;
    if (source.includes('[name]')) return source.split('[name]').join(exampleName);
    const colonIndex = Math.max(source.indexOf(':'), source.indexOf('：'));
    if (colonIndex >= 0) return `${source.slice(0, colonIndex + 1)} ${exampleName}`;
    return source.startsWith('לדוג') ? `${source}: ${exampleName}` : exampleName;
  };

  const createGalleryProjection = (preview, ownerKey) => {
    const sourceVisual = preview.querySelector('[data-tag-preview-visual]');
    if (!sourceVisual) return null;

    const frame = document.createElement('div');
    frame.className = 'swv6-tag-preview__gallery-frame';
    frame.dataset.swv6TagProjectionOwner = ownerKey;

    const stage = document.createElement('div');
    stage.className = 'swv6-tag-preview__gallery-stage';

    const visual = sourceVisual.cloneNode(true);
    visual.classList.add('swv6-tag-preview__gallery-visual');
    stage.append(visual);
    frame.append(stage);

    const heading = preview.querySelector('.swv6-tag-preview__header')?.textContent?.trim();
    if (heading) {
      const badge = document.createElement('span');
      badge.className = 'swv6-tag-preview__gallery-label';
      badge.textContent = heading;
      frame.append(badge);
    }

    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'swv6-tag-preview__gallery-thumb';
    thumb.dataset.swv6TagProjectionOwner = ownerKey;
    thumb.setAttribute('aria-label', preview.dataset.galleryThumbnailLabel || 'תצוגה אישית');

    const thumbImage = document.createElement('img');
    thumbImage.className = 'swv5-gallery__thumb-image';
    thumbImage.alt = '';
    thumbImage.loading = 'lazy';
    thumb.append(thumbImage);

    return {
      frame,
      thumb,
      thumbImage,
      image: visual.querySelector('[data-tag-preview-image]'),
      nameOutput: visual.querySelector('[data-tag-preview-name]'),
      phoneOutput: visual.querySelector('[data-tag-preview-phone]'),
      visual
    };
  };

  const initialize = ({ root, listen }) => {
    const previews = Array.from(root.querySelectorAll('[data-swv51-tag-preview]'));
    if (!previews.length) return false;

    const desktop = window.matchMedia(desktopQuery);
    const selectedColor = () => root.swv5Purchase?.currentColor?.()
      || root.querySelector('.swv5-option--color [data-option-value]:checked')?.value
      || '';
    const normalizeColor = (value) => root.swv5Purchase?.normalizeColor?.(value)
      || window.SafeWalkV51AccessoryVariants?.normalizeColor?.(value)
      || String(value || '').trim().toLowerCase();
    const currentColorKey = () => {
      const normalizedColor = normalizeColor(selectedColor());
      return colorKeys.includes(normalizedColor) ? normalizedColor : 'black';
    };
    const states = [];

    previews.forEach((preview, index) => {
      const panel = preview.closest('.swv51-personalization');
      const controls = Array.from(panel?.querySelectorAll('input[name], input[data-safewalk-personalization-key]') || []);
      const nameInput = controls.find((input) => input.dataset.safewalkPersonalizationKey === 'dog_name'
        || input.name === fieldNames.dog_name);
      const phoneInput = controls.find((input) => input.dataset.safewalkPersonalizationKey === 'phone'
        || input.name === fieldNames.phone);
      const sourceTarget = {
        image: preview.querySelector('[data-tag-preview-image]'),
        nameOutput: preview.querySelector('[data-tag-preview-name]'),
        phoneOutput: preview.querySelector('[data-tag-preview-phone]'),
        visual: preview.querySelector('[data-tag-preview-visual]')
      };
      if (!panel || !sourceTarget.nameOutput || !sourceTarget.phoneOutput || !sourceTarget.visual) return;

      const ownerKey = preview.dataset.previewOwner || `preview-${index + 1}`;
      const virtualMediaId = `swv6-tag-preview-${ownerKey}`;
      const originalNamePlaceholder = nameInput?.getAttribute('placeholder') || '';
      const phonePlaceholder = preview.dataset.phonePlaceholder || '';
      const labelTemplates = {
        empty: preview.dataset.accessibilityLabelEmptyTemplate || '',
        nameOnly: preview.dataset.accessibilityLabelNameTemplate || '[name]',
        phoneOnly: preview.dataset.accessibilityLabelPhoneTemplate || '[phone]',
        both: preview.dataset.accessibilityLabelTemplate || '[name] [phone]'
      };
      let projection = null;
      let restoreProjectionOnDesktop = false;

      const exampleName = (colorKey) => preview.dataset[dataKey('exampleName', colorKey)]
        || preview.dataset.nameFallback
        || preview.dataset.namePlaceholder
        || '';
      const previewSource = (colorKey) => preview.dataset[dataKey('previewImage', colorKey)]
        || preview.dataset.previewImageBlack
        || '';
      const targets = () => projection ? [sourceTarget, projection] : [sourceTarget];
      const values = () => ({
        name: (nameInput?.value || '').trim(),
        phone: (phoneInput?.value || '').trim()
      });

      const render = () => {
        const { name, phone } = values();
        const hasName = Boolean(name);
        const hasPhone = Boolean(phone);
        const layout = hasName && hasPhone
          ? 'both'
          : hasName
            ? 'name-only'
            : hasPhone
              ? 'phone-only'
              : 'empty';
        const visibleName = layout === 'empty' ? exampleName(currentColorKey()) : name;
        const visiblePhone = layout === 'empty' ? phonePlaceholder : phone;
        const labelTemplate = layout === 'name-only'
          ? labelTemplates.nameOnly
          : layout === 'phone-only'
            ? labelTemplates.phoneOnly
            : labelTemplates[layout];

        preview.dataset.layout = layout;
        preview.dataset.hasName = String(hasName);
        preview.dataset.hasPhone = String(hasPhone);
        targets().forEach((target) => {
          target.nameOutput.textContent = visibleName;
          target.phoneOutput.textContent = visiblePhone;
          target.visual.closest('.swv6-tag-preview__gallery-frame')?.setAttribute('data-layout', layout);
          target.visual.setAttribute('aria-label', interpolate(labelTemplate, visibleName, visiblePhone));
          fitLine(target.nameOutput);
          fitLine(target.phoneOutput);
        });
      };

      const syncColor = () => {
        const colorKey = currentColorKey();
        const source = previewSource(colorKey);
        preview.dataset.previewColor = colorKey;
        if (nameInput) nameInput.placeholder = personalizedPlaceholder(originalNamePlaceholder, exampleName(colorKey));
        targets().forEach((target) => {
          if (target.image && source && target.image.getAttribute('src') !== source) target.image.setAttribute('src', source);
        });
        if (projection?.thumbImage && source) projection.thumbImage.src = source;
        render();
      };

      const activateGalleryPreview = () => {
        if (!desktop.matches || !projection || !root.swv5Gallery) return;
        root.swv5Gallery.setMedia(virtualMediaId, true, 'personalization');
        window.requestAnimationFrame(render);
      };
      const restoreEmptyPreview = () => {
        window.setTimeout(() => {
          const activeElement = document.activeElement;
          const fieldFocused = activeElement === nameInput || activeElement === phoneInput;
          const { name, phone } = values();
          if (!fieldFocused && !name && !phone && root.swv5Gallery?.isActive(virtualMediaId)) {
            root.swv5Gallery.restoreNative(false);
          }
        }, 0);
      };
      const setActiveField = (fieldKind) => {
        preview.dataset.activeField = fieldKind;
        if (projection) projection.frame.dataset.activeField = fieldKind;
        activateGalleryPreview();
      };
      const clearActiveField = (fieldKind) => {
        if (preview.dataset.activeField === fieldKind) preview.removeAttribute('data-active-field');
        if (projection?.frame.dataset.activeField === fieldKind) projection.frame.removeAttribute('data-active-field');
        restoreEmptyPreview();
      };
      const unregisterProjection = () => {
        if (projection && root.swv5Gallery?.isActive(virtualMediaId)) restoreProjectionOnDesktop = true;
        if (projection) root.swv5Gallery?.unregisterVirtualMedia(virtualMediaId);
        projection = null;
        preview.removeAttribute('data-desktop-gallery-source');
      };
      const registerProjection = () => {
        if (projection || !desktop.matches || !root.swv5Gallery) return;
        root.swv5Gallery.unregisterVirtualMedia(virtualMediaId);
        root.querySelectorAll('[data-swv6-tag-projection-owner]').forEach((node) => {
          if (node.dataset.swv6TagProjectionOwner === ownerKey) node.remove();
        });
        const candidate = createGalleryProjection(preview, ownerKey);
        if (!candidate) return;
        const registered = root.swv5Gallery.registerVirtualMedia({
          id: virtualMediaId,
          frame: candidate.frame,
          thumb: candidate.thumb,
          preserveOnVariantChange: true
        });
        if (!registered) return;
        projection = candidate;
        preview.dataset.desktopGallerySource = 'customer';
        syncColor();
        const fieldFocused = document.activeElement === nameInput || document.activeElement === phoneInput;
        if (restoreProjectionOnDesktop || fieldFocused) {
          restoreProjectionOnDesktop = false;
          activateGalleryPreview();
        }
      };
      const syncBreakpoint = () => {
        if (desktop.matches) registerProjection();
        else unregisterProjection();
      };

      [nameInput, phoneInput].forEach((input) => {
        if (!input) return;
        listen(input, 'input', () => {
          render();
          activateGalleryPreview();
        });
        listen(input, 'change', () => {
          render();
          activateGalleryPreview();
        });
      });
      if (nameInput) {
        listen(nameInput, 'focus', () => setActiveField('dog_name'));
        listen(nameInput, 'blur', () => clearActiveField('dog_name'));
      }
      if (phoneInput) {
        listen(phoneInput, 'focus', () => setActiveField('phone'));
        listen(phoneInput, 'blur', () => clearActiveField('phone'));
      }
      listen(root, 'swv5:gallery-ready', syncBreakpoint);
      listen(desktop, 'change', syncBreakpoint);
      listen(window, 'resize', render);

      states.push({
        syncColor,
        syncBreakpoint,
        cleanup: () => {
          unregisterProjection();
          if (nameInput) nameInput.setAttribute('placeholder', originalNamePlaceholder);
        }
      });
      syncColor();
      syncBreakpoint();
    });

    listen(root, 'swv5:variant-change', () => states.forEach((state) => state.syncColor()));
    return () => states.reverse().forEach((state) => state.cleanup());
  };

  if (window.SafeWalkV51Product?.register) {
    window.SafeWalkV51Product.register(featureName, initialize);
    return;
  }

  window.SafeWalkV51ProductQueue = window.SafeWalkV51ProductQueue || [];
  window.SafeWalkV51ProductQueue.push([featureName, initialize]);
})();
